import {
  HttpStatus,
  ResponseFormatModel,
} from "@/app/lib/models/ResponseFormatModel";
import { MemberDetailsModel } from "@/app/lib/models/MemberDetailsModel";
import { getAuthHeaders, getBaseUrl } from "@/app/services/api.utils";
import { deduplicatedFetch } from "@/app/services/api_base.service";
import logger from "@/lib/logger";

const BASE_PATH = getBaseUrl() + "/v1/members";

/**
 * Get One member for trainee or admin
 * @param email_id
 * @param sessionId
 * @param member_id
 */
export const getMemberDetailsApi = async (
  email_id: string,
  member_id: string,
  sessionId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };
  try {
    let headers: any = await getAuthHeaders(sessionId, email_id);

    headers["email_id"] = email_id;
    headers["member_id"] = member_id;

    const resp = await deduplicatedFetch(BASE_PATH + "/member", {
      method: "GET",
      headers: headers,
      cache: "no-cache",
      next: { revalidate: 5 },
    });
    if (resp && resp.status === HttpStatus.OK) {
      const data = await resp.json();
      response = new ResponseFormatModel(resp.status, resp.statusText, data);
    } else {
      throw new Error("Error in getMemberDetailsApi");
    }
  } catch (error) {
    logger.error("Error in getMemberDetailsApi", { error: String(error) });
    // throw new Error(error);
  }
  return response;
};

/**
 * Get all members for trainee
 * @param email_id
 * @param role
 * @param sessionId
 */
export const getAllMembersApi = async (
  email_id: string,
  role: string,
  sessionId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  } as ResponseFormatModel;
  try {
    let headers: any = await getAuthHeaders(sessionId, email_id);
    let resp;
    headers["email_id"] = email_id;
    resp = await deduplicatedFetch(BASE_PATH, {
      method: "GET",
      headers: headers,
      cache: "no-cache",
      next: { revalidate: 5 },
    });
    if (resp) {
      const data = await resp.json();
      response = new ResponseFormatModel(resp.status, resp.statusText, data);
    } else {
      throw new Error("Error in getMembersForGuardianApi");
    }
  } catch (error) {
    logger.error("Error in getMembersForGuardianApi", { error: String(error) });
    throw new Error(error);
  }
  return response;
};
/**
 * Get all members for trainee
 * @param email_id
 * @param sessionId
 * @param guardian_id
 */
export const getAllMembersForTraineeApi = async (
  email_id: string,
  guardian_id?: string,
  sessionId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  } as ResponseFormatModel;
  try {
    let headers: any = await getAuthHeaders(sessionId, email_id);
    headers["email_id"] = email_id;
    headers["trainee_id"] = email_id;
    if (guardian_id) {
      headers["guardian_id"] = guardian_id;
    }
    const resp = await deduplicatedFetch(BASE_PATH, {
      method: "GET",
      headers: headers,
      cache: "no-cache",
      next: { revalidate: 60 },
    });

    if (resp && resp.status === HttpStatus.OK) {
      const data = await resp.json();
      response = new ResponseFormatModel(resp.status, resp.statusText, data);
    } else {
      throw new Error("Error in getAllMembersForTraineeApi");
    }
  } catch (error) {
    logger.error("Error in getAllMembersForTraineeApi", {
      error: String(error),
    });
    throw new Error(error);
  }

  return response;
};

/**
 * Get image URL fro each member_Id
 * @param email_Id
 * @param member_Id
 * @param sessionId
 */
export const getDetailOfEachMember = async (
  email_Id: string,
  member_Id: string,
  sessionId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };

  try {
    let headers: any = await getAuthHeaders(sessionId, email_Id);
    headers["email_id"] = email_Id;
    headers["member_id"] = member_Id;
    const resp = await deduplicatedFetch(BASE_PATH + "/member", {
      method: "GET",
      headers: headers,
      cache: "no-cache",
      next: { revalidate: 60 },
    });
    if (resp && resp.status === HttpStatus.OK) {
      const data = await resp.json();
      response = new ResponseFormatModel(resp.status, resp.statusText, data);
    } else {
      throw new Error("Error in getDetailOfEachMember");
    }
  } catch (error) {
    logger.error("Error in getDetailOfEachMember", { error: String(error) });
    // throw new Error(error);
  }
  return response;
};

/**
 * Add new Guardian to the db
 * @param email_id : string Email of the guardian or Admin who is adding the member
 * @param memberDetails : MemberDetailsModel Details of the member to be added
 * @param sessionId : string Login Session ID
 */
export const addUpdateMemberApi = async (
  email_id: string,
  memberDetails: MemberDetailsModel,
  sessionId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };
  try {
    let headers: any = await getAuthHeaders(sessionId, email_id);
    headers["email_id"] = email_id;
    headers["Content-Type"] = "application/json";

    const resp = await deduplicatedFetch(`${BASE_PATH}/addOrUpdateMember`, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(memberDetails),
    });

    const text = await resp.text();
    let parsed: any;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }

    if (resp.ok) {
      response = new ResponseFormatModel(resp.status, resp.statusText, parsed);
    } else {
      logger.error("Error in addNewMemberApi", { parsed });
      response = new ResponseFormatModel(resp.status, resp.statusText, parsed);
    }
  } catch (error) {
    logger.error("Error in addNewMemberApi", { error: String(error) });
    response = new ResponseFormatModel(500, "Server Error", null);
  }

  return response;
};

/**
 * Delete a Member
 * @param email_id : string Email of the user deleting (Guardian/Admin)
 * @param member_id : string Member ID to delete
 * @param guardian_id : string Guardian ID of the member
 * @param sessionId : string Login Session ID
 */
export const deleteMemberApi = async (
  email_id: string,
  member_id: string,
  guardian_id: string,
  sessionId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };
  try {
    let headers: any = await getAuthHeaders(sessionId, email_id);
    headers["email_id"] = email_id;
    headers["Content-Type"] = "application/json";

    const resp = await deduplicatedFetch(`${BASE_PATH}/deleteMember`, {
      method: "POST",
      headers: headers,
      body: JSON.stringify({
        member_id: member_id,
        guardian_id: guardian_id,
      }),
    });

    const text = await resp.text();
    let parsed: any;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }

    if (resp.ok) {
      response = new ResponseFormatModel(resp.status, resp.statusText, parsed);
    } else {
      logger.error("Error in deleteMemberApi", parsed);
      response = new ResponseFormatModel(resp.status, resp.statusText, parsed);
    }
  } catch (error) {
    logger.error("Error in deleteMemberApi", error);
    response = new ResponseFormatModel(500, "Server Error", null);
  }
  return response;
};

/**
 * Add Event to Member(s)
 * @param email_id : string Email of the guardian/admin adding the event
 * @param eventData : EventDetailsModel Event details including member_id(s), event_title, etc.
 * @param sessionId : string Login Session ID
 */
export const addEventToMembersApi = async (
  email_id: string,
  eventData: {
    event_id?: string;
    member_id: string[] | string;
    custom_contact_names?: string;
    guardian_id?: string;
    trainee_id?: string;
    event_title?: string;
    event_description?: string;
    date?: string | null;
    location?: string;
    observation?: string;
    followUp_actions?: string;
    media_url?: string;
    level?: number;
    success_indicator?: string;
    event_type?: string[];
    duration?: string;
  },
  sessionId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };
  try {
    let headers: any = await getAuthHeaders(sessionId, email_id);
    headers["email_id"] = email_id;
    headers["Content-Type"] = "application/json";

    let memberIds: string[] = [];
    if (Array.isArray(eventData.member_id)) {
      memberIds = eventData.member_id.filter(
        (id) => id && id.trim().length > 0,
      );
    } else if (
      eventData.member_id &&
      typeof eventData.member_id === "string" &&
      eventData.member_id.trim().length > 0
    ) {
      memberIds = [eventData.member_id];
    }

    const results: Array<{
      member_id: string;
      success: boolean;
      data?: any;
      error?: any;
    }> = [];

    // Single API call with member_ids array - one event for all selected members
    if (memberIds.length > 0) {
      const sharedEventId =
        eventData.event_id ||
        `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      logger.info(
        `[addEventToMembersApi] ${eventData.event_id ? "Updating" : "Creating"} event (${sharedEventId}) for ${memberIds.length} contact(s):`,
        memberIds,
      );

      const eventPayload = {
        member_ids: memberIds,
        event_id: sharedEventId,
        event_title: eventData.event_title,
        event_description: eventData.event_description,
        event_date: eventData.date || new Date().toISOString(),
        location: eventData.location,
        observations: eventData.observation,
        follow_up_actions: eventData.followUp_actions,
        media_urls: eventData.media_url ? [eventData.media_url] : [],
        event_type: Array.isArray(eventData.event_type)
          ? eventData.event_type.join(",")
          : eventData.event_type,
        duration: eventData.duration,
        success_indicator:
          eventData.success_indicator === "true" ||
          eventData.success_indicator === "yes",
        custom_contact_names: eventData.custom_contact_names,
        trainee_id: eventData.trainee_id,
      };

      const resp = await deduplicatedFetch(`${BASE_PATH}/addEventToMember`, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(eventPayload),
      });

      const text = await resp.text();
      let parsed: any;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = text;
      }

      if (resp.ok && parsed?.success) {
        results.push(
          ...memberIds.map((mid) => ({
            member_id: mid,
            success: true,
            data: parsed,
          })),
        );
      } else {
        results.push(
          ...memberIds.map((mid) => ({
            member_id: mid,
            success: false,
            error: parsed,
          })),
        );
        logger.error(`Error adding event for members:`, parsed);
      }
    }

    // If custom contact names provided but no members selected, create a placeholder event
    if (
      eventData.custom_contact_names &&
      eventData.custom_contact_names.trim().length > 0 &&
      memberIds.length === 0
    ) {
      // For custom contacts, we'll store them in the event data
      // You might want to create a special member or handle this differently
      const customEventPayload = {
        ...(eventData.event_id && { event_id: eventData.event_id }),
        member_id: "custom_contact", // Placeholder
        event_title: eventData.event_title,
        event_description: eventData.event_description,
        event_date: eventData.date || new Date().toISOString(),
        location: eventData.location,
        observations: eventData.observation,
        follow_up_actions: eventData.followUp_actions,
        custom_contact_names: eventData.custom_contact_names,
        trainee_id: eventData.trainee_id,
      };

      const resp = await deduplicatedFetch(`${BASE_PATH}/addEventToMember`, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(customEventPayload),
      });

      const text = await resp.text();
      let parsed: any;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = text;
      }

      if (resp.ok) {
        results.push({
          member_id: "custom_contact",
          success: true,
          data: parsed,
        });
      } else {
        results.push({
          member_id: "custom_contact",
          success: false,
          error: parsed,
        });
      }
    }

    const allSuccessful = results.every((r) => r.success);
    const allFailed = results.every((r) => !r.success);

    if (allSuccessful && results.length > 0) {
      response = new ResponseFormatModel(
        HttpStatus.OK,
        "Events added successfully",
        results,
      );
    } else if (allFailed) {
      response = new ResponseFormatModel(
        HttpStatus.BadRequest,
        "Failed to add events",
        results,
      );
    } else {
      // 206 Partial Content - some events succeeded, some failed
      response = new ResponseFormatModel(
        HttpStatus.PartialContent,
        "Some events added successfully",
        results,
      );
    }
  } catch (error) {
    logger.error("Error in addEventToMembersApi", error);
    response = new ResponseFormatModel(500, "Server Error", null);
  }
  return response;
};

/**
 * Get All Events for a Member
 * @param email_id : string Email of the user requesting the events
 * @param sessionId : string Login Session ID
 */
export const getAllEvents = async (
  email_id: string,
  sessionId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };
  try {
    let headers: any = await getAuthHeaders(sessionId, email_id);
    headers["email_id"] = email_id;
    headers["Content-Type"] = "application/json";

    const resp = await deduplicatedFetch(`${BASE_PATH}/get-events`, {
      method: "POST",
      headers: headers,
    });

    const text = await resp.text();
    let parsed: any;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }

    const message =
      parsed && typeof parsed === "object" && "message" in parsed
        ? String((parsed as { message?: string }).message)
        : resp.statusText || "Request failed";

    if (resp.ok) {
      response = ResponseFormatModel.fromStatusCode(
        resp.status,
        message,
        parsed,
      );
    } else {
      if (resp.status === 401) {
        logger.warn("getAllEvents unauthorized", { email_id, message });
      } else {
        logger.error("Error in getAllEvents", parsed);
      }
      response = ResponseFormatModel.fromStatusCode(
        resp.status,
        message,
        parsed,
      );
    }
  } catch (error) {
    logger.error("Error in getAllEvents", error);
    response = new ResponseFormatModel(
      HttpStatus.InternalServerError,
      "Server Error",
      null,
    );
  }
  return response;
};

/**
 * Get Event by ID for a Member
 * @param email_id : string Email of the user requesting the event
 * @param member_id : string Member ID
 * @param event_id : string Event ID
 * @param sessionId : string Login Session ID
 */
export const getEventByIdApi = async (
  email_id: string,
  event_id: string,
  member_id?: string,
  sessionId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };
  try {
    let headers: any = await getAuthHeaders(sessionId, email_id);
    headers["email_id"] = email_id;
    headers["Content-Type"] = "application/json";

    const body: { event_id: string; member_id?: string } = { event_id };
    if (member_id) body.member_id = member_id;

    const resp = await deduplicatedFetch(`${BASE_PATH}/get-event-by-id`, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(body),
    });

    const text = await resp.text();
    let parsed: any;
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }

    if (resp.ok) {
      response = new ResponseFormatModel(resp.status, resp.statusText, parsed);
    } else {
      logger.error("Error in getEventByIdApi", parsed);
      response = new ResponseFormatModel(resp.status, resp.statusText, parsed);
    }
  } catch (error) {
    logger.error("Error in getEventByIdApi", error);
    response = new ResponseFormatModel(500, "Server Error", null);
  }
  return response;
};

/**
 * Get Events for a Member
 * @param email_id : string Email of the user requesting the event
 * @param member_id : string Member ID
 * @param sessionId : string Login Session ID
 */
export const getEventByMemberIdApi = async (
  email_id: string,
  member_id: string,
  sessionId?: string,
): Promise<ResponseFormatModel> => {
  let response: ResponseFormatModel = {
    message: "",
    status: HttpStatus.BadRequest,
  };
  try {
    let headers: any = await getAuthHeaders(sessionId, email_id);
    headers["email_id"] = email_id;
    headers["Content-Type"] = "application/json";

    const resp = await deduplicatedFetch(`${BASE_PATH}/get-event-for-member`, {
      method: "POST",
      headers: headers,
      body: JSON.stringify({
        member_id: member_id,
      }),
    });

    const data = await resp.text();
    let parsed: any;
    try {
      parsed = JSON.parse(data);
    } catch {
      parsed = data;
    }

    if (resp.ok) {
      response = new ResponseFormatModel(resp.status, resp.statusText, parsed);
    } else {
      logger.error("Error in getEventByIdApi", parsed);
      response = new ResponseFormatModel(resp.status, resp.statusText, parsed);
    }
  } catch (error) {
    logger.error("Error in getEventByIdApi", error);
    response = new ResponseFormatModel(500, "Server Error", null);
  }
  return response;
};
